import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Factory, MapPin, Handshake, Award } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import api, { imgUrl } from "../lib/api";
import SEO from "../components/SEO";
import ProductCard from "../components/ProductCard";
import { Reveal, MaskedWords } from "../components/motion/Reveal";
import Marquee from "../components/Marquee";
import { useTheme } from "../context/ThemeContext";
import { DEFAULT_THEME } from "../lib/theme";

const HERO = "https://images.unsplash.com/photo-1648881806148-e5c51179c826?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxsdXh1cmlvdXMlMjBsaXZpbmclMjByb29tJTIwd2l0aCUyMG1ldGFsJTIwZGVjb3J8ZW58MHx8fHwxNzg1OTY5MjAzfDA&ixlib=rb-4.1.0&q=85";
const ARTISAN = "https://images.unsplash.com/photo-1681407979977-ea6060c802f6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwzfHxhcnRpc2FuJTIwd29ya2luZyUyMG9uJTIwbWV0YWx8ZW58MHx8fHwxNzg1OTY5MjAzfDA&ixlib=rb-4.1.0&q=85";

const HERO_HEIGHTS = { small: "h-[50vh]", default: "h-[70vh] md:h-[80vh]", large: "h-[90vh]" };
const TRUST_ICONS = [Award, MapPin, Handshake, Factory];

export default function Home() {
  const { theme, content, seo, loaded } = useTheme();
  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [promo, setPromo] = useState(null);
  const [newArrivals, setNewArrivals] = useState([]);
  const [saleItems, setSaleItems] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroImgY = useTransform(scrollYProgress, [0, 1], ["-4%", "8%"]);
  const heroImgScale = useTransform(scrollYProgress, [0, 1], [1.18, 1.28]);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/products/featured").then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/campaigns/promo").then((r) => setPromo(r.data?.id ? r.data : null)).catch(() => {});
    api.get("/products?new_arrival=1&limit=8").then((r) => setNewArrivals(r.data.items || [])).catch(() => {});
    api.get("/products?sale=1&limit=8").then((r) => setSaleItems(r.data.items || [])).catch(() => {});
  }, []);

  const siteOrigin = window.location.origin;
  const orgSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${siteOrigin}/#organization`,
        name: "HUMAIRA CRAFTS",
        alternateName: "Humaira Crafts",
        url: siteOrigin,
        logo: { "@type": "ImageObject", url: `${siteOrigin}/api/favicon`, width: 512, height: 512 },
        description: "Premium metal home décor manufacturer in Delhi since 2002",
        foundingDate: "2002",
        address: { "@type": "PostalAddress", addressLocality: "Delhi", addressCountry: "IN" },
      },
      {
        "@type": "WebSite",
        "@id": `${siteOrigin}/#website`,
        url: siteOrigin,
        name: "HUMAIRA CRAFTS",
        alternateName: "Humaira Crafts",
        publisher: { "@id": `${siteOrigin}/#organization` },
        inLanguage: "en",
      },
    ],
  };

  const heroImg = theme.hero_image ? imgUrl(theme.hero_image) : HERO;
  const heritageImg = theme.heritage_image ? imgUrl(theme.heritage_image) : ARTISAN;
  const secList = theme.sections?.length ? theme.sections : DEFAULT_THEME.sections;
  const sec = (id) => {
    const idx = secList.findIndex((s) => s.id === id);
    const found = secList.find((s) => s.id === id);
    const c = found?.content || {};
    return {
      style: {
        order: idx === -1 ? 99 : idx + 1,
        ...(found && !found.visible ? { display: "none" } : {}),
        ...(c.bg_color ? { backgroundColor: c.bg_color } : {}),
        ...(c.text_align ? { textAlign: c.text_align } : {}),
      },
    };
  };
  const sContent = (id) => secList.find((s) => s.id === id)?.content || {};
  // Only owner-uploaded images (our storage / same origin) may render — external stock URLs are treated as no image.
  const catImage = (cat) => {
    const img = cat.image || "";
    if (cat.show_image === false) return "";
    return img.startsWith("/api/files") || img.startsWith(window.location.origin) ? img : "";
  };

  return (
    <div className="flex flex-col" data-testid="home-page">
      <SEO title={seo.home_title || null} description={seo.home_description || "Premium metal home décor manufacturer in Delhi since 2002. Candle holders, bowls, trays, vases & luxury décor. Wholesale & retail."} schema={orgSchema} image={seo.og_image ? imgUrl(seo.og_image) : heroImg} />

      {theme.hero_enabled && (
      <section ref={heroRef} className="relative overflow-hidden" style={{ order: 0 }} data-testid="hero-section" data-eid="hero-section" data-etype="section">
        {!loaded ? (
          <div className={`w-full bg-[#EFEBE3] ${HERO_HEIGHTS[theme.hero_height] || HERO_HEIGHTS.default}`} data-testid="hero-skeleton" />
        ) : (
        <>
        {theme.hero_mobile_image && (
          <motion.img src={imgUrl(theme.hero_mobile_image)} alt="Luxury metal home décor by Humaira Crafts" className={`md:hidden w-full ${HERO_HEIGHTS[theme.hero_height] || HERO_HEIGHTS.default}`} style={{ objectFit: theme.hero_fit || "cover", objectPosition: theme.hero_position || "center", y: heroImgY, scale: heroImgScale }} />
        )}
        <motion.img src={heroImg} alt="Luxury metal home décor by Humaira Crafts" className={`w-full ${HERO_HEIGHTS[theme.hero_height] || HERO_HEIGHTS.default} ${theme.hero_mobile_image ? "hidden md:block" : ""}`} style={{ objectFit: theme.hero_fit || "cover", objectPosition: theme.hero_position || "center", y: heroImgY, scale: heroImgScale }} data-eid="hero-image" data-etype="image" />
        <div className="absolute inset-0" style={{ backgroundColor: `rgba(0,0,0,${(theme.hero_overlay ?? 30) / 100})` }} />
        {theme.hero_layout === "minimal" ? (
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent">
            <div className="max-w-7xl mx-auto px-4 md:px-8 pb-14 pt-32">
              <div className="max-w-xl">
                <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="small-caps text-gold font-semibold mb-4">Manufacturing Since 2002</motion.p>
                <h1 className="text-4xl md:text-6xl tracking-tighter leading-none font-semibold text-[#FAF9F6]" data-eid="hero-heading" data-etype="text">
                  <MaskedWords text={settings?.hero_title || "The Art of Metal, Perfected Since 2002"} />
                </h1>
                <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.55, ease: [0.22, 1, 0.36, 1] }} className="text-sm md:text-base text-[#D4D4D4] mt-6 leading-relaxed" data-eid="hero-subtitle" data-etype="text">
                  {settings?.hero_subtitle || "Premium handcrafted metal home décor, manufactured in Delhi."}
                </motion.p>
                <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.75, ease: [0.22, 1, 0.36, 1] }} className="flex flex-wrap gap-4 mt-8">
                  <Link to={content.hero_primary_link || "/shop"} className="bg-gold text-[#111] px-8 py-3 small-caps hover:bg-[#FAF9F6] transition-colors duration-300 flex items-center gap-2" data-testid="hero-shop-button" data-eid="hero-btn-primary" data-etype="button">
                    {content.hero_primary_text} <ArrowRight strokeWidth={1.5} size={14} />
                  </Link>
                  <Link to={content.hero_secondary_link || "/wholesale"} className="border border-[#FAF9F6] text-[#FAF9F6] px-8 py-3 small-caps hover:bg-[#FAF9F6] hover:text-[#111] transition-colors duration-300" data-testid="hero-wholesale-button" data-eid="hero-btn-secondary" data-etype="button">
                    {content.hero_secondary_text}
                  </Link>
                </motion.div>
              </div>
            </div>
          </div>
        ) : (
          <div className={`absolute inset-0 flex ${theme.hero_layout === "center" ? "items-center justify-center px-4" : "items-end md:items-center"}`}>
            <div className={`max-w-7xl mx-auto px-4 md:px-8 w-full ${theme.hero_layout === "center" ? "flex justify-center" : "pb-12 md:pb-0"}`}>
              <div className={`max-w-xl bg-white/70 backdrop-blur-xl border border-white/40 p-8 md:p-12 ${theme.hero_layout === "center" ? "text-center" : ""}`} style={{ textAlign: theme.hero_text_align || undefined }}>
                <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="small-caps text-[#8A6D1F] font-semibold mb-4">Manufacturing Since 2002</motion.p>
                <h1 className="text-4xl md:text-6xl tracking-tighter leading-none font-semibold" data-eid="hero-heading" data-etype="text">
                  <MaskedWords text={settings?.hero_title || "The Art of Metal, Perfected Since 2002"} />
                </h1>
                <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.55, ease: [0.22, 1, 0.36, 1] }} className="text-sm md:text-base text-muted-foreground mt-6 leading-relaxed" data-eid="hero-subtitle" data-etype="text">
                  {settings?.hero_subtitle || "Premium handcrafted metal home décor, manufactured in Delhi."}
                </motion.p>
                <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.75, ease: [0.22, 1, 0.36, 1] }} className={`flex flex-wrap gap-4 mt-8 ${theme.hero_layout === "center" || theme.hero_text_align === "center" ? "justify-center" : theme.hero_text_align === "right" ? "justify-end" : ""}`}>
                  <Link to={content.hero_primary_link || "/shop"} className="bg-[#111] text-[#FAF9F6] px-8 py-3 small-caps hover:bg-gold hover:text-[#111] transition-colors duration-300 flex items-center gap-2" data-testid="hero-shop-button" data-eid="hero-btn-primary" data-etype="button">
                    {content.hero_primary_text} <ArrowRight strokeWidth={1.5} size={14} />
                  </Link>
                  <Link to={content.hero_secondary_link || "/wholesale"} className="border border-[#111] px-8 py-3 small-caps hover:bg-[#111] hover:text-[#FAF9F6] transition-colors duration-300" data-testid="hero-wholesale-button" data-eid="hero-btn-secondary" data-etype="button">
                    {content.hero_secondary_text}
                  </Link>
                </motion.div>
              </div>
            </div>
          </div>
        )}
        </>
        )}
      </section>
      )}

      <div style={{ order: 1 }} data-eid="marquee-section" data-etype="section">
        <Marquee />
      </div>

      {promo?.show_banner && (
        <section className="relative overflow-hidden bg-[#111]" style={{ order: 1 }} data-testid="promo-banner" data-eid="promo-banner" data-etype="section">
          {promo.banner_image && <img src={imgUrl(promo.banner_image)} alt={promo.title || promo.name} className="absolute inset-0 w-full h-full object-cover opacity-40" />}
          <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20 text-center">
            <Reveal>
              <p className="small-caps text-gold mb-3">Limited Time Offer</p>
              <h2 className="text-3xl md:text-5xl tracking-tight font-semibold text-[#FAF9F6]" data-eid="promo-banner-heading" data-etype="text">{promo.title || promo.name}</h2>
              {promo.description && <p className="text-sm md:text-base text-[#D4D4D4] mt-4 max-w-xl mx-auto" data-eid="promo-banner-text" data-etype="text">{promo.description}</p>}
              <Link to={promo.cta_link || "/shop"} className="inline-block mt-7 bg-gold text-[#111] px-9 py-3.5 small-caps hover:bg-[#FAF9F6] transition-colors duration-300" data-testid="promo-banner-cta" data-eid="promo-banner-cta" data-etype="button">
                {promo.cta_text || "Shop the Offer"}
              </Link>
            </Reveal>
          </div>
        </section>
      )}

      <section className="border-b border-black/10" data-testid="trust-bar" data-eid="trust-section" data-etype="section" {...sec("trust")}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 grid grid-cols-2 md:grid-cols-4 gap-8">
          {content.trust_items.map((t, i) => {
            const Icon = TRUST_ICONS[i % TRUST_ICONS.length];
            return (
            <Reveal key={t.title} delay={i * 0.08} className="flex flex-col items-start gap-4 border-t border-black/15 pt-6">
              <div className="flex w-full items-start justify-between">
                <Icon strokeWidth={1.5} size={26} className="text-gold" />
                <span className="font-serif text-5xl leading-none text-transparent select-none" style={{ WebkitTextStroke: "1px var(--gold)" }}>{String(i + 1).padStart(2, "0")}</span>
              </div>
              <p className="font-serif text-lg font-semibold">{t.title}</p>
              <p className="text-xs text-muted-foreground">{t.text}</p>
            </Reveal>
            );
          })}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 py-24 w-full" data-testid="categories-section" data-eid="categories-section" data-etype="section" {...sec("categories")}>
        <Reveal className="flex items-end justify-between mb-12">
          <div>
            <p className="small-caps text-gold mb-3">{sContent("categories").subheading || content.categories_subheading}</p>
            <h2 className="text-3xl md:text-4xl tracking-tight font-semibold" data-eid="categories-heading" data-etype="text">{sContent("categories").heading || content.categories_heading}</h2>
          </div>
          <Link to="/shop" className="small-caps border-b border-black hover:border-gold hover:text-gold transition-colors duration-300 hidden md:block" data-testid="view-all-link">View All</Link>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {categories.slice(0, 8).map((cat, i) => (
            <Link
              key={cat.id}
              to={`/category/${cat.slug}`}
              className={`group relative overflow-hidden border border-black/10 ${i % 5 === 0 ? "md:row-span-2" : ""}`}
              data-testid={`category-tile-${cat.slug}`}
            >
              {catImage(cat) ? (
                <img
                  src={imgUrl(cat.image)}
                  alt={cat.image_alt || `${cat.name} — premium metal home décor by Humaira Crafts`}
                  loading="lazy"
                  className={`w-full object-cover transition-transform duration-500 group-hover:scale-105 ${i % 5 === 0 ? "aspect-[3/4] md:h-full" : "aspect-square"}`}
                />
              ) : (
                <div className={`w-full flex items-center justify-center bg-[#F1ECE2] ${i % 5 === 0 ? "aspect-[3/4] md:h-full" : "aspect-square"}`}>
                  <span className="font-serif text-7xl md:text-8xl leading-none text-transparent select-none" style={{ WebkitTextStroke: "1.5px var(--gold)" }}>{cat.name.charAt(0)}</span>
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <p className="absolute bottom-4 left-4 text-[#FAF9F6] font-serif text-lg md:text-xl">{cat.name}</p>
            </Link>
          ))}
        </div>
      </section>

      {featured.length > 0 && (
        <section className="bg-white border-y border-black/10" data-testid="featured-section" data-eid="featured-section" data-etype="section" {...sec("featured")}>
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-24">
            <Reveal>
              <p className="small-caps text-gold mb-3">{sContent("featured").subheading || content.featured_subheading}</p>
              <h2 className="text-3xl md:text-4xl tracking-tight font-semibold mb-12" data-eid="featured-heading" data-etype="text">{sContent("featured").heading || content.featured_heading}</h2>
            </Reveal>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {newArrivals.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 py-24 w-full" data-testid="new-arrivals-section" data-eid="new-arrivals-section" data-etype="section" {...sec("new_arrivals")}>
          <Reveal>
            <p className="small-caps text-gold mb-3">{sContent("new_arrivals").subheading || "Just In"}</p>
            <h2 className="text-3xl md:text-4xl tracking-tight font-semibold mb-12" data-eid="new-arrivals-heading" data-etype="text">{sContent("new_arrivals").heading || "New Arrivals"}</h2>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {newArrivals.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}

      {saleItems.length > 0 && (
        <section className="bg-white border-y border-black/10" data-testid="sale-section" data-eid="sale-section" data-etype="section" {...sec("sale")}>
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-24">
            <Reveal>
              <p className="small-caps text-gold mb-3">{sContent("sale").subheading || "On Sale"}</p>
              <h2 className="text-3xl md:text-4xl tracking-tight font-semibold mb-12" data-eid="sale-heading" data-etype="text">{sContent("sale").heading || "Festive Offers"}</h2>
            </Reveal>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {saleItems.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </div>
        </section>
      )}

      <section className="bg-[#111111] text-[#FAF9F6]" data-testid="heritage-section" data-eid="heritage-section" data-etype="section" {...sec("heritage")}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-24 grid md:grid-cols-2 gap-16 items-center">
          <Reveal y={40} className="overflow-hidden">
            <img src={heritageImg} alt="Artisan handcrafting metal décor at the Humaira Crafts Delhi facility" loading="lazy" className="w-full aspect-[4/3] object-cover hover:scale-105 transition-transform duration-700" data-eid="heritage-image" data-etype="image" />
          </Reveal>
          <Reveal delay={0.15}>
            <p className="small-caps text-gold mb-4"><span className="opacity-60 mr-2">N° 01 ·</span>{sContent("heritage").subheading || content.heritage_subheading}</p>
            <h2 className="text-3xl md:text-4xl tracking-tight font-semibold leading-tight text-[#FAF9F6]" data-eid="heritage-heading" data-etype="text">{sContent("heritage").heading || content.heritage_heading}</h2>
            <p className="text-[#A3A3A3] mt-6 leading-relaxed text-sm md:text-base" data-eid="heritage-paragraph" data-etype="text">
              {sContent("heritage").paragraph || content.heritage_paragraph}
            </p>
            <div className="grid grid-cols-3 gap-6 mt-10 border-t border-white/10 pt-10">
              {content.heritage_stats.map((s) => (
                <div key={s.label}><p className="font-serif text-3xl text-gold">{s.value}</p><p className="text-xs text-[#A3A3A3] mt-1">{s.label}</p></div>
              ))}
            </div>
            <Link to={sContent("heritage").button_link || "/about"} className="inline-block mt-10 border border-[#FAF9F6] px-8 py-3 small-caps hover:bg-gold hover:text-[#111] hover:border-gold transition-colors duration-300" data-testid="heritage-about-button" data-eid="heritage-button" data-etype="button">
              {sContent("heritage").button_text || content.heritage_button}
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 py-24 text-center w-full" data-testid="wholesale-cta" data-eid="cta-section" data-etype="section" {...sec("wholesale_cta")}>
        <Reveal>
        <p className="small-caps text-gold mb-4"><span className="opacity-60 mr-2">N° 02 ·</span>{sContent("wholesale_cta").subheading || content.wholesale_cta_subheading}</p>
        <h2 className="text-3xl md:text-4xl tracking-tight font-semibold max-w-2xl mx-auto" data-eid="cta-heading" data-etype="text">{sContent("wholesale_cta").heading || content.wholesale_cta_heading}</h2>
        <p className="text-muted-foreground mt-4 text-sm md:text-base" data-eid="cta-text" data-etype="text">{sContent("wholesale_cta").paragraph || content.wholesale_cta_text}</p>
        <Link to={sContent("wholesale_cta").button_link || "/wholesale"} className="inline-block mt-8 bg-[#111] text-[#FAF9F6] px-10 py-4 small-caps hover:bg-gold hover:text-[#111] transition-colors duration-300" data-testid="wholesale-cta-button" data-eid="cta-button" data-etype="button">
          {sContent("wholesale_cta").button_text || content.wholesale_cta_button}
        </Link>
        </Reveal>
      </section>
    </div>
  );
}
